
import React from 'react';
import { ProjectStatus } from '../types';
import type { Project } from '../types';
import { TrendingUp, Activity, Map as MapIcon, BarChart3, PieChart } from 'lucide-react';

interface StatsOverviewProps {
  projects: Project[];
}

const PROJECT_CHART_COLORS = ['#3B82F6', '#10B981', '#6366F1', '#F59E0B', '#EF4444']; 

export const StatsOverview: React.FC<StatsOverviewProps> = ({ projects }) => {
  const stats = {
    total: projects.length,
    active: projects.filter(p => p.status === ProjectStatus.IN_PROGRESS).length,
    completed: projects.filter(p => p.status === ProjectStatus.COMPLETED).length,
    tendering: projects.filter(p => p.status === ProjectStatus.UNDER_TENDERING).length,
    totalKm: projects.reduce((acc, curr) => acc + (curr.kilometers || 0), 0),
    avgProgress: projects.length > 0 
      ? Math.round(projects.reduce((acc, curr) => acc + (curr.progress || 0), 0) / projects.length) 
      : 0
  };

  const statusData = [
    { name: 'In Progress', value: projects.filter(p => p.status === ProjectStatus.IN_PROGRESS).length, color: PROJECT_CHART_COLORS[0] },
    { name: 'Completed', value: projects.filter(p => p.status === ProjectStatus.COMPLETED).length, color: PROJECT_CHART_COLORS[1] },
    { name: 'Tendering', value: projects.filter(p => p.status === ProjectStatus.UNDER_TENDERING).length, color: PROJECT_CHART_COLORS[2] },
    { name: 'Planning', value: projects.filter(p => p.status === ProjectStatus.PLANNING).length, color: PROJECT_CHART_COLORS[3] },
    { name: 'Paused', value: projects.filter(p => p.status === ProjectStatus.PAUSED).length, color: PROJECT_CHART_COLORS[4] },
  ].filter(d => d.value > 0);

  // Calculate Pie Chart Segments
  const totalProjects = statusData.reduce((sum, item) => sum + item.value, 0);
  let accumulatedPercent = 0;
  
  // Group by region for bar chart
  const regionData = projects.reduce((acc, curr) => {
    const existing = acc.find(item => item.name === curr.region);
    if (existing) {
      existing.projects += 1;
    } else {
      acc.push({ name: curr.region, projects: 1 });
    }
    return acc;
  }, [] as { name: string; projects: number }[]).sort((a, b) => b.projects - a.projects).slice(0, 5);

  const maxProjectsInRegion = Math.max(...regionData.map(r => r.projects), 1);

  return (
    <div className="space-y-6 mb-8">
      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white p-5 rounded-2xl shadow-sm border border-gray-100 flex items-center justify-between hover:shadow-md transition-shadow">
          <div>
            <p className="text-sm text-gray-500 font-medium mb-1">Total Projects</p>
            <h3 className="text-2xl font-bold text-gray-900">{stats.total}</h3>
          </div>
          <div className="p-3 bg-blue-50 text-blue-600 rounded-xl">
            <BarChart3 size={24} />
          </div>
        </div>

        <div className="bg-white p-5 rounded-2xl shadow-sm border border-gray-100 flex items-center justify-between hover:shadow-md transition-shadow">
          <div>
            <p className="text-sm text-gray-500 font-medium mb-1">Active Construction</p>
            <h3 className="text-2xl font-bold text-gray-900">{stats.active}</h3>
          </div>
           <div className="p-3 bg-yellow-50 text-yellow-600 rounded-xl">
            <Activity size={24} />
          </div>
        </div>

        <div className="bg-white p-5 rounded-2xl shadow-sm border border-gray-100 flex items-center justify-between hover:shadow-md transition-shadow">
          <div>
             <p className="text-sm text-gray-500 font-medium mb-1">Total Distance</p>
            <h3 className="text-2xl font-bold text-gray-900">{stats.totalKm.toFixed(1)} <span className="text-sm font-normal text-gray-400">km</span></h3>
          </div>
          <div className="p-3 bg-purple-50 text-purple-600 rounded-xl">
            <MapIcon size={24} />
          </div>
        </div>

        <div className="bg-white p-5 rounded-2xl shadow-sm border border-gray-100 flex items-center justify-between hover:shadow-md transition-shadow">
          <div>
            <p className="text-sm text-gray-500 font-medium mb-1">Avg. Progress</p>
            <h3 className="text-2xl font-bold text-gray-900">{stats.avgProgress}%</h3>
          </div>
          <div className="p-3 bg-green-50 text-green-600 rounded-xl">
            <TrendingUp size={24} />
          </div>
        </div>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Status Chart */}
        <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100 flex flex-col">
            <h3 className="text-lg font-bold text-gray-900 mb-6">Project Status Distribution</h3>
            <div className="flex-grow flex items-center justify-center relative min-h-[250px]">
                {totalProjects > 0 ? (
                  <div className="relative">
                      {/* SVG Donut Chart */}
                      <svg viewBox="0 0 36 36" className="w-48 h-48 transform -rotate-90">
                        {statusData.map((item, index) => {
                            const percent = (item.value / totalProjects) * 100;
                            const dashArray = `${percent} ${100 - percent}`;
                            const dashOffset = -accumulatedPercent;
                            accumulatedPercent += percent;
                            return (
                                <circle
                                    key={index}
                                    cx="18" 
                                    cy="18" 
                                    r="15.915"
                                    fill="transparent"
                                    stroke={item.color}
                                    strokeWidth="3"
                                    strokeDasharray={dashArray}
                                    strokeDashoffset={dashOffset}
                                />
                            );
                        })}
                      </svg>
                      {/* Center Text */}
                       <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                            <span className="text-3xl font-bold text-gray-800">{totalProjects}</span>
                            <span className="text-xs text-gray-500 uppercase">Projects</span>
                       </div>
                  </div>
                ) : (
                   <div className="text-gray-400 flex flex-col items-center">
                      <PieChart size={48} className="mb-2 opacity-20"/>
                      <span>No data available</span>
                   </div>
                )}
            </div>
            {/* Legend */}
            <div className="mt-6 grid grid-cols-2 gap-2">
                {statusData.map((item, index) => (
                    <div key={index} className="flex items-center text-sm">
                        <span className="w-3 h-3 rounded-full mr-2" style={{ backgroundColor: item.color }}></span>
                        <span className="text-gray-600 truncate">{item.name}</span>
                        <span className="ml-auto font-medium text-gray-900">{item.value}</span>
                    </div>
                ))}
            </div>
        </div>

        {/* Region Chart */}
        <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
            <h3 className="text-lg font-bold text-gray-900 mb-6">Top Regions by Activity</h3>
             <div className="flex flex-col justify-center h-full min-h-[250px] space-y-5">
                {regionData.length > 0 ? (
                   regionData.map((item, index) => (
                       <div key={index}>
                           <div className="flex justify-between text-sm mb-1.5 font-medium">
                               <span className="text-gray-700">{item.name}</span>
                               <span className="text-gray-900">{item.projects} projects</span>
                           </div>
                           <div className="w-full bg-gray-100 rounded-full h-2.5 overflow-hidden">
                               <div 
                                    className="h-full rounded-full bg-blue-500 transition-all duration-1000" 
                                    style={{ width: `${(item.projects / maxProjectsInRegion) * 100}%` }}
                                ></div>
                           </div>
                       </div>
                   ))
                ) : (
                    <div className="text-center text-gray-400 py-10">No region data available</div>
                )}
            </div>
        </div>
      </div>
    </div>
  );
};
